import { lazy, type ReactNode } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router'
import LayoutAdmin from '@/app/layouts/layout-admin'
import Dashboard from '@/app/pages/dashboard'
import Unauthorized from '@/app/pages/unauthorized'
import { retryImport } from '@/app/router/retry-import'
import { PATHS, SEGMENTS } from '@/app/router/routes'
import RouterAccounts from '@/features/account/router/router-account'
import useAuth from '@/features/auth/hooks/use-auth'
import { PermissionBusiness } from '@/features/roles/consts/permissions'
import { TYPE_ACCOUNT } from '@/shared/catalogs/type-account'

// Cada ruta lazy entra por `retryImport`: un despliegue borra los chunks que esta pestaña
// todavía nombra, y sin esto la primera navegación después de desplegar muere en un 404.
const RouterClients = lazy(() =>
  retryImport(() => import('@/features/clients/router/router-clients'))
)
const RouterBusiness = lazy(() => retryImport(() => import('@/app/router/router-business')))
const RouterNegotiations = lazy(() =>
  retryImport(() => import('@/features/negotiations/components/router-negotiations'))
)
const RouterDeliveries = lazy(() =>
  retryImport(() => import('@/features/deliveries/components/router-deliveries'))
)
const RouterInventory = lazy(() =>
  retryImport(() => import('@/features/inventory/routers/inventory.router'))
)
const ChangelogPage = lazy(() => retryImport(() => import('@/app/pages/changelog.page')))

export const NavigateAuthBusinessAdmin = ({
  children,
  isRouter = false
}: {
  children?: ReactNode | ReactNode[]
  isRouter?: boolean
}) => {
  const { auth } = useAuth()

  if (auth?.typeAccount === TYPE_ACCOUNT.admin) return isRouter ? <Outlet /> : children
  const inclueSomePermissionOfBusiness = auth?.permissions?.some((permission) =>
    Object.keys(PermissionBusiness).includes(permission)
  )
  if (inclueSomePermissionOfBusiness) return isRouter ? <Outlet /> : children
  return isRouter ? <Navigate to={PATHS.admin.unauthorized} /> : null
}

const RouterAdmin = () => {
  return (
    <Routes>
      <Route element={<LayoutAdmin />}>
        <Route path="/" element={<Navigate to={SEGMENTS.admin.dashboard} />} />
        <Route path={`${SEGMENTS.admin.dashboard}/*`} element={<Dashboard />} />
        <Route element={<NavigateAuthBusinessAdmin isRouter />}>
          <Route path={`${SEGMENTS.admin.business}/*`} element={<RouterBusiness />} />
        </Route>
        <Route path={`${SEGMENTS.admin.negotiations}/*`} element={<RouterNegotiations />} />
        <Route path={`${SEGMENTS.admin.deliveries}/*`} element={<RouterDeliveries />} />
        <Route path={`${SEGMENTS.admin.inventory}/*`} element={<RouterInventory />} />
        <Route path={`${SEGMENTS.admin.clients}/*`} element={<RouterClients />} />
        <Route path={`${SEGMENTS.account.root}/*`} element={<RouterAccounts />} />
        <Route path={SEGMENTS.admin.changelog} element={<ChangelogPage />} />
        <Route path={SEGMENTS.admin.unauthorized} element={<Unauthorized />} />
        <Route path="*" element={<h1>Not Found</h1>} />
      </Route>
    </Routes>
  )
}

export default RouterAdmin
